import { useContext, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { WriteBatch } from "firebase/firestore";
import dayjs from "dayjs";
import { AccountStatuses, BreadAccount } from "breadcommon";
import { InstitutionsContext } from "../firebaseio/InstitutionsContext";
import { UserContext } from "../firebaseio/UserContext";
import { firestoreUpdateAccount } from "../firebaseio/firestoreIo";
import { HorizontalSpacer } from "../utils/Utils";
import { Icon } from "../Icon";
import { buildClasses } from "../utils/buildClasses";
import { InstitutionLogo } from "../common/InstitutionLogo";

export function CandidateAccountRow(props: {
  account: BreadAccount;
  accountUpdatePromises: React.MutableRefObject<Promise<void | WriteBatch>[]>;
}): JSX.Element {
  const [isChecked, setIsChecked] = useState<boolean>(true);
  const institutions = useContext(InstitutionsContext);
  const user = useContext(UserContext);

  const horizontalSepAmt = 20;

  let institution = null;
  if (
    props.account.institution_id !== null &&
    institutions.has(props.account.institution_id)
  ) {
    institution = institutions.get(props.account.institution_id);
  }

  return (
    <div
      className={buildClasses(
        "flex",
        "items-center",
        "p-5",
        "border-b",
        "border-on-surface-200",
        "last:border-b-0",
      )}
    >
      <InstitutionLogo
        institution={institution}
        size={40}
        className={buildClasses({ if: !isChecked, then: "opacity-40" })}
      />
      <HorizontalSpacer width={horizontalSepAmt} />
      <div
        className={buildClasses("w-[75px]", "truncate", {
          if: isChecked,
          then: "text-on-surface-800",
          else: "text-on-surface-300",
        })}
      >
        {institution?.name}
      </div>
      <HorizontalSpacer width={horizontalSepAmt} />
      <div
        className={buildClasses("w-[100px]", "truncate", {
          if: isChecked,
          then: "text-on-surface-800",
          else: "text-on-surface-300",
        })}
      >
        {props.account.name}
      </div>
      <HorizontalSpacer width={horizontalSepAmt} />
      <div
        className={buildClasses("w-[50px]", "truncate", {
          if: isChecked,
          then: "text-on-surface-500",
          else: "text-on-surface-300",
        })}
      >
        {"..." + props.account.mask}
      </div>
      <HorizontalSpacer width={horizontalSepAmt} />
      <div
        className={buildClasses("w-[75px]", "truncate", {
          if: isChecked,
          then: "text-on-surface-500",
          else: "text-on-surface-300",
        })}
      >
        {props.account.subtype}
      </div>
      <HorizontalSpacer width={horizontalSepAmt} />
      <div className={buildClasses("w-[170px]")}>
        <DatePicker
          defaultValue={props.account.sync_start_date}
          onChange={(newValue) => {
            const p = firestoreUpdateAccount(user.uid, props.account.id, {
              manual_data: {
                sync_start_timestamp_secs: newValue?.isValid()
                  ? newValue.unix()
                  : dayjs().subtract(1, "month").unix(),
              },
            });
            props.accountUpdatePromises.current.push(p);
          }}
          label="Sync start date"
          disabled={!isChecked}
          format="MM/DD/YY"
          slotProps={{
            textField: { size: "small" },
            popper: {
              sx: {
                zIndex: 1000000001,
              },
            },
          }}
        />
      </div>
      <HorizontalSpacer width={horizontalSepAmt} />
      <div
        className={buildClasses(
          "flex",
          "justify-center",
          "items-center",
          "w-5",
          "h-5",
          "flex-shrink-0",
          "flex-grow-0",
          "rounded",
          "border",
          "cursor-pointer",
          "ml-auto",
          "transition-colors",
          {
            if: isChecked,
            then: buildClasses(
              "border-[#7d57ff]",
              "bg-[#7d57ff]",
              "text-white",
              "hover:border-[#6c48f2]",
              "hover:bg-[#6c48f2]",
            ),
            else: buildClasses(
              "border-on-surface-300",
              "bg-white",
              "text-transparent",
              "hover:border-on-surface-400",
              "hover:bg-on-surface-50",
            ),
          },
        )}
        onClick={() => {
          if (isChecked) {
            const p = firestoreUpdateAccount(user.uid, props.account.id, {
              system_data: { status: AccountStatuses.NEW_UNSELECTED },
            });
            props.accountUpdatePromises.current.push(p);
          } else {
            const p = firestoreUpdateAccount(user.uid, props.account.id, {
              system_data: { status: AccountStatuses.NEW_SELECTED },
            });
            props.accountUpdatePromises.current.push(p);
          }
          setIsChecked(!isChecked);
        }}
      >
        {isChecked && <Icon name={"done"} size={16} />}
      </div>
    </div>
  );
}
